/**
 * Единая точка работы с датами.
 *
 * Бэкенд отдаёт и принимает UTC (формат 2026-10-23T08:00:00+00:00).
 * SPA обязан показывать и вводить время по Москве (UTC+3) независимо от таймзоны
 * браузера, поэтому конвертация идёт через хелперы ниже, а не через локальную таймзону.
 *
 * Отображение через `| date` настроено глобально в app.config.ts
 * (DATE_PIPE_DEFAULT_OPTIONS с timezone MOSCOW_UTC_OFFSET).
 */

/** Москва с 2014 года не переводит часы, поэтому фиксированного смещения достаточно. */
export const MOSCOW_UTC_OFFSET_MINUTES = 180;

/** Формат смещения для DatePipe и DATE_PIPE_DEFAULT_OPTIONS. */
export const MOSCOW_UTC_OFFSET = '+0300';

/** Идентификатор зоны для Intl и сторонних календарей. */
export const MOSCOW_TIME_ZONE = 'Europe/Moscow';

const MOSCOW_OFFSET_MS = MOSCOW_UTC_OFFSET_MINUTES * 60_000;

/** Дата-время без указания смещения: бэкенд такое отдаёт как UTC. */
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

const pad = (n: number, length = 2) => String(n).padStart(length, '0');

export type DateLike = Date | string | number | null | undefined;

/** То, что реально приходит из API: Date среди сгенерированных моделей не бывает. */
export type ApiDateValue = string | number | null | undefined;

/**
 * Разбор значения, пришедшего из API.
 * Строка без смещения трактуется как UTC — бэкенд других не присылает.
 */
export function parseApiDate(value: DateLike): Date | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
        const fromNumber = new Date(value);
        return isNaN(fromNumber.getTime()) ? null : fromNumber;
    }

    const raw = value.trim();
    const normalized = NAIVE_DATE_TIME.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw;
    const parsed = new Date(normalized);

    return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Календарные поля Date (getFullYear/getHours/...) заполнены московским временем —
 * возвращает реальный момент времени.
 *
 * Применять к значениям, пришедшим из датапикеров и любого пользовательского ввода.
 */
export function fromMoscowWallClock(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()) - MOSCOW_OFFSET_MS);
}

/**
 * Обратная операция: момент времени → Date, локальные поля которого читаются
 * как московское время. Такой Date отдаётся в p-datepicker и FullCalendar.
 */
export function toMoscowWallClock(date: Date): Date {
    const shifted = new Date(date.getTime() + MOSCOW_OFFSET_MS);

    return new Date(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), shifted.getUTCHours(), shifted.getUTCMinutes(), shifted.getUTCSeconds(), shifted.getUTCMilliseconds());
}

/**
 * Значение из API → Date для датапикера (поля читаются как московское время).
 *
 * Принимает только сырое значение из API: Date сюда попадать не должен, иначе
 * сдвиг применится второй раз.
 */
export function apiDateToPickerValue(value: ApiDateValue): Date | null {
    const parsed = parseApiDate(value);
    return parsed === null ? null : toMoscowWallClock(parsed);
}

/**
 * Дата-время для отправки на бэкенд: реальный UTC со смещением.
 * "2026-10-23T08:00:00.000Z"
 */
export function toUtcISOString(date: Date): string {
    return fromMoscowWallClock(date).toISOString();
}

/**
 * Календарный день по Москве, без времени и таймзоны: "2026-10-23".
 * Для полей типа Date, где момент времени не имеет смысла.
 */
export function toDateOnlyUTC(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Начало московских суток указанной даты в UTC. */
export function startOfDayUtc(date: Date): string {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    return toUtcISOString(dayStart);
}

/** Конец московских суток указанной даты в UTC (инклюзивная верхняя граница). */
export function endOfDayUtc(date: Date): string {
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);
    return toUtcISOString(dayEnd);
}

/** Начало следующих московских суток в UTC (эксклюзивный конец диапазона). */
export function nextDayStartUtc(date: Date): string {
    const nextDayStart = new Date(date);
    nextDayStart.setHours(0, 0, 0, 0);
    nextDayStart.setDate(nextDayStart.getDate() + 1);
    return toUtcISOString(nextDayStart);
}

/**
 * Дата-время в UTC в формате, который ждёт бэкенд для значений полей форм:
 * "yyyy-MM-dd HH:mm".
 */
export function toBackendDateTime(date: Date): string {
    const utc = fromMoscowWallClock(date);
    return `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())} ${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())}`;
}

/**
 * Форматирование Date, который уже несёт московский wall clock — значение
 * датапикера, день календаря и т.п.
 *
 * К таким значениям нельзя применять глобальный DatePipe: он сдвинет их ещё раз
 * на +3. Локальные поля тут и есть московское время, поэтому Intl вызывается
 * без timeZone.
 */
export function formatWallClock(date: Date, options: Intl.DateTimeFormatOptions, locale = 'ru-RU'): string {
    return new Intl.DateTimeFormat(locale, options).format(date);
}

/** Форматирование значения из API (UTC) по Москве — там, где нельзя воспользоваться DatePipe. */
export function formatMoscow(value: DateLike, options: Intl.DateTimeFormatOptions, locale = 'ru-RU'): string {
    const parsed = parseApiDate(value);
    if (parsed === null) {
        return '';
    }

    return new Intl.DateTimeFormat(locale, { ...options, timeZone: MOSCOW_TIME_ZONE }).format(parsed);
}
